import { useSession } from 'next-auth/react'

import { PAGE_TYPE, PAGE_TYPE_GPT } from '@/constants/pageTypes'
import { getSearchTypeWithKeywords } from '@/domain/srp/getSearchTypeWithKeywords'
import { type FilterGroup } from '@/generated'
import useCookiePreferences from '@/hooks/useCookiePreferences'
import { type RouteLocale } from '@/hooks/useLocale'
import {
  createAutosBodyLabel,
  createAutosModelLabel,
  createAutosYearLabel,
  createCentralMakeLabel,
  createCentralModelLabel,
  createCentralYearLabel,
  createForSaleByLabel,
  createPriceLabel,
} from '@/lib/ads/components/gpt/attributesGpt'
import {
  createHeaderBiddingLabel,
  createHostnameLabel,
  createOptOutTargetingConfig,
  createPrebidVersionLabel,
  createTestTargetConfig,
  createWebappName,
  createWebVersionTarget,
} from '@/lib/ads/components/gpt/configGpt'
import {
  createDeviceLabel,
  createHashedDeviceIdLabel,
  createPtgLabel,
} from '@/lib/ads/components/gpt/deviceGpt'
import {
  createPageTargetLabel,
  createPageTypeLabel,
  createPageTypeLabelKj,
} from '@/lib/ads/components/gpt/pageGpt'
import {
  addTrackingCategories,
  createCategoryIdLabel,
  createLocationIdLabel,
  createSearchKeywordTarget,
} from '@/lib/ads/components/gpt/searchGpt'
import {
  createHashedUserIdLabel,
  createLanguageLabel,
  createUserLoggedInLabel,
  createUserLoggedInLabelKj,
} from '@/lib/ads/components/gpt/userGpt'
import { type srpGpt } from '@/lib/ads/types/adConfig'
import { getFilterSelectedValues, getIsAuthenticatedForGpt } from '@/lib/ads/utils/gptTargeting'
import { debug } from '@/utils/debug'

import { removeEmptyValues } from '../utils/removeEmptyValues'

export type UseSrpGptProps = {
  categories?: string[]
  categoryIds?: string[]
  currentPage: number
  filters: FilterGroup[]
  keywords: string
  locationId?: number
  routeLocale: RouteLocale
  totalListings?: number
}

// https://wiki.es.ecg.tools/pages/viewpage.action?pageId=160806983

export const useSrpGpt = ({
  categories = [],
  categoryIds,
  currentPage,
  filters,
  keywords,
  locationId,
  routeLocale,
  totalListings = 0,
}: UseSrpGptProps): srpGpt[] => {
  const { status: authenticationStatus } = useSession()
  const { hasUserOptedOutTargeting = false } = useCookiePreferences()

  const pageType =
    totalListings > 0 ? getSearchTypeWithKeywords(keywords) : PAGE_TYPE.ZERO_SEARCH_RESULTS_PAGE

  const isAuthenticated = getIsAuthenticatedForGpt(authenticationStatus)
  const pageTypeLabelKj = PAGE_TYPE_GPT[pageType]

  // values regarding the user's device
  const device = [createHashedDeviceIdLabel(), createDeviceLabel(), createPtgLabel()]

  // values regarding the user
  const user = [
    createLanguageLabel(routeLocale),
    createUserLoggedInLabel(isAuthenticated),
    createUserLoggedInLabelKj(isAuthenticated),
    createHashedUserIdLabel(),
  ]

  // values regarding the page
  const page = [
    createPageTypeLabel(keywords),
    createPageTypeLabelKj(pageTypeLabelKj),
    createPageTargetLabel(currentPage),
  ]

  // global values regarding the platform
  const config = [
    createPrebidVersionLabel(),
    createHostnameLabel(),
    createHeaderBiddingLabel(),
    createTestTargetConfig(),
    createWebappName(),
    createWebVersionTarget(),
    createOptOutTargetingConfig(hasUserOptedOutTargeting),
    // { key: GPT_KEYS.AD_BLOCKER_LABEL, value: '0' }
  ]

  // values regarding the search context
  const search = [
    createSearchKeywordTarget(keywords),
    createCategoryIdLabel(categoryIds),
    createLocationIdLabel(locationId),
    ...addTrackingCategories(categories),
  ]

  const yearFilters = getFilterSelectedValues(filters, 'yearSection')
  const forSaleByFilters = getFilterSelectedValues(filters, 'forSaleBySection')
  const makeFilters = getFilterSelectedValues(filters, 'makeSection')
  const bodyFilters = getFilterSelectedValues(filters, 'bodyTypeSection')
  const priceFilters = getFilterSelectedValues(filters, 'priceSection')
  const modelFilters = getFilterSelectedValues(filters, 'modelSection')

  // values regarding the attributes selected by the filters
  const attributes = [createPriceLabel(priceFilters), createForSaleByLabel(forSaleByFilters)]

  // values regarding the autos attributes selected by the filters
  const attributesAutos = [
    createAutosYearLabel(yearFilters),
    createCentralMakeLabel(makeFilters),
    createAutosBodyLabel(bodyFilters),
    createCentralYearLabel(yearFilters),
    createCentralModelLabel(modelFilters),
    createAutosModelLabel(modelFilters),
  ]

  const gptKeyValues = removeEmptyValues([
    // page
    ...page,

    // config
    ...config,

    // device info
    ...device,

    // user
    ...user,

    // search context
    ...search,

    // attributes
    ...attributes,

    // autos attributes
    ...attributesAutos,

    // { key: GPT_KEYS.LAST_SEARCH_KEYWORD_LABEL, value: 'headphones' },
    // { key: GPT_KEYS.SUB_CITY_TARGETING_LABEL, value: 'ottawa' }, - differs from city
    // { key: GPT_KEYS.CITY_TARGETING_LABEL, value: 'ottawa-gatineauarea' }, - differs from sub city
    // { key: GPT_KEYS.PROVINCE_TARGETING_LABEL, value: 'ontario' },
  ])

  const generateGptTable = () => {
    // eslint-disable-next-line no-console
    console.table(gptKeyValues)
  }

  debug(generateGptTable)

  return gptKeyValues
}
